/**
 * HTTP client for the Kimene API.
 *
 * Two decisions shape this file. First, authentication rides on an HttpOnly
 * cookie, so the browser never holds a token it could leak — which means every
 * request is `credentials: 'same-origin'` and there is no Authorization header
 * to manage. Second, every mutation carries a CSRF token read from a
 * non-HttpOnly companion cookie, the standard double-submit pattern that the
 * cookie-only scheme requires.
 */

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Field-level validation messages, keyed by field path. */
  readonly fields?: Record<string, string>
  /** Correlation id from the response, so a UI error can be matched to a log. */
  readonly correlationId?: string

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string>,
    correlationId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
    this.correlationId = correlationId
  }

  /** True when signing in again would plausibly fix it. */
  get isAuthError(): boolean {
    return this.status === 401
  }

  /** True when a retry could succeed — a transient server or gateway problem,
   *  as opposed to a request the server will always refuse. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500
  }
}

const CSRF_COOKIE = 'kimene_csrf'
// Must match auth.CSRFHeaderName in internal/auth/cookie.go. These disagreed
// once, and the symptom was every mutation returning 403 while reads worked.
const CSRF_HEADER = 'X-CSRF-Token'

/**
 * Sign-in is the one route where a 401 is an answer rather than a session that
 * ended, so the session-ended handler must not fire for it. Routing to the
 * sign-in screen on `totp_required` would unmount the form and discard the
 * state that reveals the second-factor field — the operator would be sent back
 * to the beginning of the very step they were being asked to complete.
 */
function isSignIn(path: string): boolean {
  return path === '/auth/login'
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  /**
   * A body sent as-is, under its own content type, instead of `body`'s JSON.
   *
   * It exists for the terminal's image attachments. Base64 inside a JSON
   * document would inflate every screenshot by a third and buy nothing: the
   * server sniffs the bytes to decide what the file is, so there is no envelope
   * for the encoding to serve.
   */
  raw?: Blob
  signal?: AbortSignal
  /** Idempotency key for a mutation, so an ambiguous network failure can be
   *  retried without creating the thing twice. */
  idempotencyKey?: string
  query?: Record<string, string | number | boolean | undefined>
}

/** Called when the server reports the session has ended, so the app can route
 *  to the sign-in screen rather than each caller handling 401 individually. */
let onUnauthorized: (() => void) | undefined
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn
}

/**
 * Reports an ended session discovered somewhere other than a REST response.
 *
 * The event socket learns it first and most reliably — the hub re-checks the
 * credential on a timer and closes with a typed code — while a console sitting
 * on one screen may make no request for minutes. Routing that through the same
 * handler is what stops the two from disagreeing about whether the operator is
 * still signed in.
 */
export function reportUnauthorized(): void {
  onUnauthorized?.()
}

/**
 * Re-reads the identity endpoint purely for its `Set-Cookie`.
 *
 * The server re-derives the session's CSRF token there from the nonce it
 * stored, so this restores a cookie that went missing or ended up bound to a
 * different session. It is deterministic, so calling it cannot invalidate a
 * token another request is already carrying.
 */
async function repairCSRF(): Promise<void> {
  try {
    await fetch('/api/v1/auth/session', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
  } catch {
    // The retry will fail the same way and report honestly. Nothing to add.
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}, repaired = false): Promise<T> {
  const { method = 'GET', body, raw, signal, idempotencyKey, query } = opts

  let url = `/api/v1${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') params.set(k, String(v))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (raw !== undefined) headers['Content-Type'] = raw.type || 'application/octet-stream'
  else if (body !== undefined) headers['Content-Type'] = 'application/json'

  // Safe methods do not need a CSRF token, and sending one on every GET would
  // widen the surface for no benefit.
  if (method !== 'GET') {
    const token = readCookie(CSRF_COOKIE)
    if (token) headers[CSRF_HEADER] = token
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
  }

  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'same-origin',
      body: raw ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    })
  } catch (err) {
    // An AbortError is the caller cancelling, not a failure to report.
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'network', 'Cannot reach the server.')
  }

  if (res.status === 204) return undefined as T

  const correlationId = res.headers.get('X-Correlation-Id') ?? undefined
  const contentType = res.headers.get('Content-Type') ?? ''
  const isJSON = contentType.includes('application/json')
  const payload: unknown = isJSON ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    const p = (payload ?? {}) as {
      code?: string
      message?: string
      fields?: Record<string, string>
    }
    const code = p.code ?? `http_${res.status}`
    // A 401 used to be short-circuited above this point, before the body was
    // read, and rewritten to a generic `unauthenticated`. That discarded the
    // one thing that separates an ended session from the sign-in form being
    // asked for a second factor: `totp_required` never reached the login page,
    // so the TOTP field could not be revealed and the operator was told their
    // password was wrong when it had in fact been accepted.
    if (res.status === 401 && !isSignIn(path)) onUnauthorized?.()
    // A rejected CSRF token means the handler never ran, so re-sending is safe
    // and cannot duplicate anything. It is worth one attempt because the
    // desynchronisation is repairable and the operator otherwise cannot act at
    // all — not even sign out, which is itself a POST behind this same gate.
    // Once, never in a loop: a token the server keeps refusing is a real
    // refusal and must be shown.
    if (res.status === 403 && code === 'csrf' && !repaired && method !== 'GET') {
      await repairCSRF()
      return request<T>(path, opts, true)
    }
    throw new ApiError(
      res.status,
      code,
      p.message ?? `Request failed with status ${res.status}`,
      p.fields,
      correlationId,
    )
  }

  return payload as T
}

export const api = {
  get: <T,>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T,>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, idempotencyKey }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** POSTs a file's bytes under their own content type. */
  upload: <T,>(path: string, blob: Blob, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', raw: blob, query, signal }),
}

/** Cursor-paginated list response, mirroring domain.PageResult. */
export interface PageResponse<T> {
  items: T[]
  nextCursor: string
  hasMore: boolean
}
